import { useCallback, useEffect, useState } from "react";
import { consultantApplicationApi } from "@/lib/api/consultant-application";
import { extractApiErrorMessage } from "@/lib/api/errors";
import type { SignupConsentDocument } from "@/types/consultant-application";

export interface SignupConsentsState {
  documents: SignupConsentDocument[] | null;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Loads the acknowledgements required on the last signup step (backend#863).
 * The backend owns the wording and versions, so nothing is hard-coded here.
 *
 * `enabled` defers the request until the applicant reaches the step that
 * needs it.
 */
export const useSignupConsents = (enabled: boolean): SignupConsentsState => {
  const [documents, setDocuments] = useState<SignupConsentDocument[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    consultantApplicationApi
      .getSignupConsents()
      .then(response => {
        if (!cancelled) {
          setDocuments(response.documents);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(extractApiErrorMessage(err, "Could not load the privacy statements"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, attempt]);

  const reload = useCallback(() => setAttempt(n => n + 1), []);

  return { documents, isLoading, error, reload };
};
